import * as React from "react";
import { Dropdown, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { MoveFilesForm, MoveFilesFormProps } from "./MoveFilesForm";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { collectionFolderStore } from "../CollectionFolder/CollectionFolderStore";
import { locateFileInDocuments } from "utils/locateFileInDocuments";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { EmailSendModal } from "Pages/Project/Communication/EmailSendModal/EmailSendModal";
import { emailSendModal } from "Pages/Project/Communication/utils/emailModalProps";
import { StartWorkflowWithFiles } from "../CollectionFolder/StartWorkflowWithFiles";
import { QANewQuestion } from "Pages/Project/QA/Components/QANewQuestion";
import { FolderNameForm, FolderNameFormProps } from "./FolderNameForm";
import { ModalProps } from "antd/lib/modal";
import { MoveFolderForm } from "./MoveFolderForm";
import { AccessRights } from "./AccessRights";
import {
  OnlineViewerCompareDataLayer,
  OnlineViewerCompareDataLayerProps,
} from "Components/FileDetails/OnlineViewerCompareDataLayer";
import { fileDownloadStore } from "../FileDownload/FileDownloadStore";
import { projectsDataStore, taskModalStore } from "Stores";
import { taskAttachmentsStore } from "Pages/Project/Tasks/Stores/TaskAttachmentsStore";
import { AddFolderMetadata } from "./AddFolderMetadata";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { LabelCalculator } from "../Calculator/LabelCalculator";
import { labelCalculatorStore } from "../Calculator/LabelCalculatorStore";
import { RestoreModal } from "./RestoreModal";
import { CopyDirectoryModal } from "./CopyDirectoryModal";
import { AccessRightsMatrix } from "../AccessRightsMatrix/AccessRightsMatrix";
import { emailLinkedViewStore } from "Pages/Project/Communication/Stores/EmailLinkedViewStore";
import { CreateLinkFolderModal } from "Pages/Project/Documents/Components/CreateLinkFolder";
import { ChangePositionModal } from "./ChangePositionModal";
import { documentsSearchStore } from "../Stores/DocumentsSearchStore";
import { umami } from "General/Umami";
import { FolderRelations } from "./FolderRelations/FolderRelations";
import { PrintForm } from "Components/FilePrint/Components/PrintForm";
import { ItemType } from "antd/es/menu/interface";
import { modal } from "Components/General/Modal/AntdModal";
import { dropdownPositionFix } from "Components/LibHelper/dropdownPositionFix";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { useEmailAttachments } from "utils/hooks/useEmailAttachments";
import { withLoadingMessage } from "utils/withLoadingMessage";
import { emailSubmitStore } from "Pages/Project/Communication/Stores/EmailSubmitStore";
import { ChangeFilesOwnerForm } from "./ChangeFilesOwnerForm";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";
import { isFolderItem } from "../utils/documentsItems";

export interface DocumentsTableRowActionsOpts {
  showOneFileActions?: boolean;
  showBasicFileActions?: boolean;
  showModuleFileActions?: boolean;
  showFolderActions?: boolean;
  allowedKeys?: string[]; // array of action keys that is allowed to show in actions dropdown
  simpleDownload?: boolean; // just downloads selected file instead of calling download wizard
  onRefresh?: () => Promise<void> | void; // callback to refresh table after action
}

interface DocumentsTableRowActionsProps extends DocumentsTableRowActionsOpts, WithModal {
  data: API_FileDataEntity | API_FolderDataEntity;
  children: React.ReactNode;
  currentFolderId?: number;
  isSearchActions?: boolean;
}

export const DocumentsTableRowActions = withModal(
  observer(
    ({
      data,
      children,
      currentFolderId,
      isSearchActions,
      showModal,
      onRefresh,
      showOneFileActions = true,
      showBasicFileActions = true,
      showModuleFileActions = true,
      showFolderActions = true,
      allowedKeys = [],
      simpleDownload = false,
    }: DocumentsTableRowActionsProps) => {
      const { t } = useTranslation(I18N_NS.PROJECT);
      const { checkAttachmentsAccess } = useEmailAttachments(emailSubmitStore);
      const handleMenuClick = ({ key, domEvent }) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        umami.track("document_action", { action_type: key, action_item: data.type });
        if (data.type === "FOLDER") {
          doFolderClick(key, data as API_FolderDataEntity);
        } else {
          doFileClick(key, data as API_FileDataEntity);
          if (fileDetailsStore.from === "collection_folder") {
            fileDetailsStore.hideModal();
            collectionFolderStore.toggleModal();
          }
        }
      };

      const linkedMailToolTipText = (
        <span>
          {t("This will open the ")}
          <strong>{t("original mail including attachments. ")}</strong>
          {t("You can use them to ")}
          <strong>{t("answer or forward ")}</strong>
          {t("the original mail.")}
        </span>
      );

      const handleDeleteFolderClick = async (folderId: number) => {
        try {
          const folderInfo = await documentsTableStore.getActiveItemsCountForDelete(
            projectsDataStore.projectId,
            folderId
          );
          const folderCount = folderInfo.numberActiveFolders;
          const fileCount = folderInfo.numberActiveFiles;
          const linkCount = folderInfo.numberLinks;

          modal.confirm({
            title: t("Mark as deleted"),
            content:
              folderCount || fileCount || linkCount ? (
                <>
                  <p>
                    {t(
                      // eslint-disable-next-line max-len
                      "The folder contains {{folderCount}} subfolders, {{fileCount}} files, and {{linkCount}} links which will be deleted as well. This action cannot be undone. Files and folders must be restored individually, links must be created anew.",
                      { folderCount, fileCount, linkCount }
                    )}
                  </p>
                  <p>{t("Do you want to proceed?")}</p>
                </>
              ) : (
                t("Do you really want to mark current folder as deleted?")
              ),
            okText: t("Yes"),
            cancelText: t("No"),
            zIndex: 9999,
            onOk: () => documentsTableStore.deleteFolder(folderId),
          });
        } catch (error) {
          throw error;
        }
      };

      const doFolderClick = (key: string, data: API_FolderDataEntity) => {
        const modalBaseProps: ModalProps = { footer: null };
        switch (key) {
          case "create":
            showModal(FolderNameForm, {
              modalProps: { ...modalBaseProps, title: t("New directory") },
              componentProps: { parentFolderId: data.id },
            });
            break;
          case "change_position":
            showModal(ChangePositionModal, {
              modalProps: {
                footer: null,
                maskClosable: true,
                width: "100%",
                style: { maxWidth: "740px" },
                title: t("Change position"),
              },
              componentProps: {
                initialData: data,
              },
            });
            break;
          case "rename":
            showModal<Pick<FolderNameFormProps, "initialName" | "isEdit" | "folderId" | "initialDescription">>(
              FolderNameForm,
              {
                modalProps: { ...modalBaseProps, title: t("Edit directory") },
                componentProps: {
                  initialName: data.originalname || data.name,
                  isEdit: true,
                  folderId: data.id,
                  initialDescription: data?.description,
                },
              }
            );
            break;
          case "move":
            showModal(MoveFolderForm, {
              modalProps: { ...modalBaseProps, title: t("Move directory") },
              componentProps: {
                folderId: data.id,
                stayInCurrentFolder: true,
                onSubmitSuccess: documentsTableStore.refresh,
              },
            });
            break;
          case "copy":
            showModal(CopyDirectoryModal, {
              modalProps: { ...modalBaseProps, title: t("Copy directory") },
              componentProps: {
                sourceId: data.id,
                initialFolderName: data.name,
                canCopyMetadata: data.folderActions.assignMetadata,
                copyDirectory: documentsTableStore.copyDirectory,
                canCopyAccessRights: data.folderActions.copyDirectoryWithRights,
              },
            });
            break;
          case "create_link":
            showModal(CreateLinkFolderModal, {
              modalProps: { ...modalBaseProps, title: t("Create link") },
              componentProps: {
                sourceFolderId: data.id,
                initialFolderName: data.name,
                createSymlinkFolder: documentsTableStore.createSymlinkFolder,
              },
            });
            break;
          case "delete":
            handleDeleteFolderClick(data.id);
            break;
          case "restore":
            modal.confirm({
              title: t("Restore folder"),
              content: t("Do you really want to restore the folder?"),
              okText: t("Yes"),
              cancelText: t("No"),
              onOk: () => documentsTableStore.restoreFolder(data.id),
            });
            break;
          case "delete_link":
            modal.confirm({
              title: t("Delete link"),
              content: t("Do you really want to delete the link?"),
              okText: t("Yes"),
              cancelText: t("No"),
              onOk: () =>
                documentsTableStore.deleteFolder(
                  data.id,
                  (data as API_ExternalLinkedFolder).linkId || data.symlinkDirectoryInfo?.symlinkId
                ),
            });
            break;
          case "set_rights":
            showModal(AccessRights, {
              modalProps: { ...modalBaseProps, width: "100%", style: { maxWidth: 1200 }, title: t("Rights") },
              componentProps: { folderId: data.id, isReadAccess: data.folderActions.readAccessRights },
            });
            break;
          case "set_labels":
            showModal(LabelCalculator, {
              modalProps: {
                title: " ",
                width: "100%",
                style: { maxWidth: 1200 },
                footer: null,
              },
              componentProps: {
                dirId: data.id,
                dirName: data.name,
              },
            });
            break;
          case "add_metadata":
            showModal(AddFolderMetadata, {
              modalProps: { ...modalBaseProps, width: "100%", style: { maxWidth: 1200 }, title: t("Add metadata") },
              componentProps: { folderId: data.id },
            });
            break;
          case "matrix":
            showModal(AccessRightsMatrix, {
              modalProps: {
                className: "viewer-modal",
                width: "100%",
                style: { maxWidth: `calc(100vw - 40px)` },
                footer: null,
                destroyOnHidden: true,
                maskClosable: false,
                keyboard: false,
                closable: false,
              },
              componentProps: { folderId: data.id },
            });
            break;
          case "relations":
            showModal(FolderRelations, {
              modalProps: { ...modalBaseProps, width: "100%", style: { maxWidth: 1200 }, title: t("Relations") },
              componentProps: { folderId: data.id },
            });
            break;
          case "jump_to_project":
            {
              const externalFolder = data as API_ExternalLinkedFolder;
              const projectId = externalFolder.sourceProjectId;
              const folderId = externalFolder.id;

              window.open(
                `${window.location.origin}/${window.GLOBAL.API_BASE}/project/${projectId}/documents/docs/${folderId}`,
                "_blank"
              );
            }
            break;
          default:
            notificationError("Error", "Unknown action" + key);
            break;
        }
      };

      const doFileClick = (key: string, data: API_FileDataEntity) => {
        const isRWMright = !data.fileActions.download && data.fileActions.downloadWithWatermark;
        let refreshCallback: () => Promise<void> | void;
        if (onRefresh) {
          refreshCallback = onRefresh;
        } else if (isSearchActions) {
          refreshCallback = documentsSearchStore.refresh;
        } else {
          refreshCallback = documentsTableStore.refreshTable;
        }
        switch (key) {
          // File actions START
          case "bcf_view":
            showModal(BCFViewerModal, {
              modalProps: {
                ...modalProps,
                maskClosable: true,
                width: "100%",
                title: `${t("File")}: ${data.name}`,
                style: { maxWidth: "1300px" },
                className: "full-height-modal with-drawer bcf-modal",
                zIndex: 1013,
              },
              componentProps: { fileId: data.id },
            });
            break;
          case "change_position":
            showModal(ChangePositionModal, {
              modalProps: {
                ...modalProps,
                maskClosable: true,
                width: "100%",
                style: { maxWidth: "740px" },
                title: t("Change position"),
              },
              componentProps: {
                initialData: data,
              },
            });
            break;
          case "easy_view":
            if (data.state === "EXTERN_SYMLINK") {
              pdfViewerStore.fetch(data.id, {
                fileName: data.name,
                canDownload: false, //externalFile.download || data.fileActions.downloadWithWatermark,
                isExternalFile: true,
                linkedFolderId: data.linkId,
              });
            } else {
              pdfViewerStore.fetch(data.id, {
                fileName: data.name,
                canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
              });
            }
            break;
          case "apryse_view":
            if (data.state === "EXTERN_SYMLINK") {
              apryseViewerStore.fetch(data.id, {
                fileName: data.name,
                canDownload: false, //externalFile.download || data.fileActions.downloadWithWatermark,
                isExternalFile: true,
                linkedFolderId: data.linkId,
              });
            } else {
              apryseViewerStore.fetch(data.id, {
                fileName: data.name,
                canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
              });
            }
            break;
          case "wopi_view":
            if (data.state === "EXTERN_SYMLINK") {
              wopiViewerStore.fetch(data.id, {
                fileName: data.name,
                linkId: data.linkId,
              });
            } else {
              wopiViewerStore.fetch(data.id, { fileName: data.name });
            }
            break;
          case "wopi_edit":
            // wopi edit is not allowed for external links
            wopiViewerStore.fetch(data.id, { fileName: data.name, edit: data.viewers.office365Edit });

            break;
          case "online_view":
            onlineViewerStore.fetch(data.id, { fileName: data.name });
            break;
          case "xkt_view":
            xktViewerStore.initViewer({
              fileName: data.name,
              fileId: data.id,
              parentFolderId: data.parentId,
              externLinkId: data.state === "EXTERN_SYMLINK" ? data.linkId : undefined,
            });
            break;
          case "online_view_compare":
            showModal<OnlineViewerCompareDataLayerProps>(OnlineViewerCompareDataLayer, {
              modalProps: {
                ...modalProps,
                width: "100%",
                title: t("Compare with..."),
                className: "full-height-modal",
                style: { maxWidth: `calc(100vw - 40px)` },
              },
              componentProps: {
                fileToCompare: data,
              },
            });
            break;
          case "task":
            taskAttachmentsStore.addProjectFile(data);
            taskModalStore.toggleModal("docs");
            break;
          case "labels":
            labelCalculatorStore.setMode("FILE");
            showModal(LabelCalculator, {
              modalProps: {
                title: " ",
                width: "100%",
                style: { maxWidth: 1200 },
                footer: null,
              },
              componentProps: { files: [data] },
            });
            break;
          case "download":
            if (simpleDownload) {
              fileDetailsStore.download(data.id);
            } else {
              let downloadRequest: API_FileRequest[];
              if (data.state === "SYMLINK") {
                downloadRequest = [{ id: data.linkId, state: data.state }];
              } else if (data.state == "EXTERN_SYMLINK") {
                downloadRequest = [{ id: data.id, state: data.state, linkId: data.linkId }];
              } else {
                downloadRequest = [{ id: data.id, state: data.state }];
              }
              withLoadingMessage(() => fileDownloadStore.initModal(downloadRequest, isRWMright ? "RWM" : "ALL"));
            }
            break;
          case "download_pdf":
            withLoadingMessage(() => fileDetailsStore.downloadAsPdf(data.id));
            break;
          case "unlock":
            withLoadingMessage(() => fileDetailsStore.unlockFile(data).then(refreshCallback));
            break;
          case "lock":
            withLoadingMessage(() => fileDetailsStore.lockFile(data).then(refreshCallback));
            break;
          case "print":
            showModal(PrintForm, {
              modalProps: { style: { maxWidth: 1300, top: 20 }, width: "100%", footer: null, title: " " },
              componentProps: {
                files: [data],
              },
            });
            break;
          case "move":
            showModal<Partial<MoveFilesFormProps>>(MoveFilesForm, {
              modalProps: { ...modalProps, title: t("Move files") },
              componentProps: {
                files: [data],
                onSubmitSuccess: documentsTableStore.goToFolder,
                defaultFolderId: documentsTableStore.currentFolderId,
                onMoveFile: documentsTableStore.moveFiles,
              },
            });
            break;
          case "create_symlink":
            showModal<Partial<MoveFilesFormProps>>(MoveFilesForm, {
              modalProps: { ...modalProps, title: t("Create link") },
              componentProps: {
                files: [data],
                onSubmitSuccess: () => documentsTableStore.refresh(),
                defaultFolderId: documentsTableStore.currentFolderId,
                onLinkFile: (files, folderId) =>
                  documentsTableStore.createFileLink(files, folderId).then(refreshCallback),
              },
            });
            break;
          case "collection_folder":
            collectionFolderStore.addFile(data as API_FileDataEntity);
            break;
          case "jump_to_dir":
            locateFileInDocuments(data.parentId, data.id);
            fileDetailsStore.hideModal();
            break;
          case "restore":
            showModal(RestoreModal, {
              modalProps: { ...modalProps, title: t("Restore") },
              componentProps: {
                files: [data],
                restoreFiles: (payload, reason) =>
                  documentsTableStore.restoreFiles(payload, reason).then(refreshCallback),
              },
            });
            break;
          case "read":
            documentsTableStore.setFilesReadStatus([data.id], false).then(refreshCallback);
            break;
          case "unread":
            documentsTableStore.setFilesReadStatus([data.id], true).then(refreshCallback);
            break;
          case "delete":
            modal.confirm({
              title: t("Mark as deleted"),
              content: t("Really mark as deleted?"),
              okText: t("Yes"),
              cancelText: t("No"),
              zIndex: 9999,
              onOk: () => documentsTableStore.deleteFiles([data]).then(refreshCallback),
            });
            break;
          case "delete_link":
            modal.confirm({
              title: t("Delete link"),
              content: t("Do you really want to delete the link?"),
              okText: t("Yes"),
              cancelText: t("No"),
              onOk: () => documentsTableStore.deleteFiles([data]).then(refreshCallback),
            });
            break;
          case "send":
            checkAttachmentsAccess([data]);
            showModal(EmailSendModal, {
              modalProps: { ...emailSendModal },
              componentProps: {
                mailType: "attach",
              },
            });
            break;
          case "ask_q":
            showModal(QANewQuestion, {
              modalProps: { ...modalProps, title: t("New question") },
              componentProps: { attachments: [data] },
            });
            break;
          case "start_workflow":
            showModal(StartWorkflowWithFiles, {
              modalProps: { ...modalProps, title: t("Start workflow") },
              componentProps: { fileIds: [data.id] },
            });
            break;
          case "linked_mail":
            emailLinkedViewStore.showLinkedMail("" + data.linkedMailId);
            break;
          case "change_owner":
            showModal(ChangeFilesOwnerForm, {
              modalProps: { ...modalProps, title: t("Change file owner") },
              componentProps: { fileIds: [data.id], onSubmitSuccess: refreshCallback },
            });
            break;
          default:
            notificationError("Error", "Unknown action" + key);
            break;
        }
      };

      const menuItems = React.useMemo(() => {
        const folderItems: ItemType[] = [];
        const oneFileItems: ItemType[] = [];
        const basicItems: ItemType[] = [];
        const moduleItems: ItemType[] = [];
        const isFolder = isFolderItem(data);

        if (isFolder) {
          const folderData = data as API_FolderDataEntity;
          const dataExternal = data as API_ExternalLinkedFolder;

          if (folderData.folderActions) {
            if (folderData.folderActions.rename) {
              folderItems.push({
                key: "rename",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "edit"]} fixedWidth={true} className="mr-5" />
                    {t("Edit")}...
                  </>
                ),
              });
            }
            if (folderData.folderActions.move) {
              folderItems.push({
                key: "move",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "dolly"]} fixedWidth={true} className="mr-5" />
                    {t("Move")}...
                  </>
                ),
              });
            }
            if (folderData.folderActions.changeFolderPosition) {
              folderItems.push({
                key: "change_position",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "sort"]} fixedWidth={true} className="mr-5" />
                    {t("Change position")}
                  </>
                ),
              });
            }
            if (folderData.folderActions.copyDirectory) {
              folderItems.push({
                key: "copy",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "copy"]} fixedWidth={true} className="mr-5" />
                    {t("Copy")}...
                  </>
                ),
              });
            }
            if (folderData.folderActions.createSymlink) {
              folderItems.push({
                key: "create_link",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "link"]} fixedWidth={true} className="mr-5" />
                    {t("Create link")}...
                  </>
                ),
              });
            }
            if (dataExternal.jumpToProject) {
              folderItems.push({
                key: "jump_to_project",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "folder"]} fixedWidth={true} />
                    {t("Jump to project")}
                  </>
                ),
              });
            }
            if (folderData.folderActions.delete) {
              if (dataExternal.linkId || folderData.symlinkDirectory) {
                folderItems.push({
                  key: "delete_link",
                  label: (
                    <>
                      <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} className="mr-5" />
                      {t("Delete link")}...
                    </>
                  ),
                });
              } else {
                folderItems.push({
                  key: "delete",
                  label: (
                    <>
                      <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} className="mr-5" />
                      {t("Mark as deleted")}...
                    </>
                  ),
                });
              }
            }
            if (folderData.folderActions.restoreFolder) {
              folderItems.push({
                key: "restore",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "trash-restore-alt"]} fixedWidth={true} className="mr-5" />
                    {t("Restore")}
                  </>
                ),
              });
            }
            if (folderData.folderActions.rightsManagement || folderData.folderActions.readAccessRights) {
              folderItems.push({
                key: "set_rights",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "key"]} fixedWidth={true} className="mr-5" />
                    {t("Rights")}...
                  </>
                ),
              });
            }
            if (
              folderData.folderActions.assignLabelRule &&
              projectsDataStore.functions?.includes("FILE_LABELS") &&
              projectsDataStore.labelsSettings?.folderLabels
            ) {
              folderItems.push({
                key: "set_labels",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "calculator"]} fixedWidth={true} className="mr-5" />
                    {t("Label calculator")}...
                  </>
                ),
              });
            }
            if (folderData.folderActions.assignMetadata) {
              folderItems.push({
                key: "add_metadata",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "file-code"]} fixedWidth={true} className="mr-5" />
                    {t("Add metadata")}...
                  </>
                ),
              });
            }
            if (folderData.folderActions.rightsMatrix) {
              folderItems.push({
                key: "matrix",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "table"]} fixedWidth={true} className="mr-5" />
                    {t("Access rights matrix")}
                  </>
                ),
              });
            }
            if (folderData.folderActions.showRelations && folderData.symlinkDirectorySource) {
              folderItems.push({
                key: "relations",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "bow-arrow"]} fixedWidth={true} className="mr-5" />
                    {t("Relations")}
                  </>
                ),
              });
            }
          }
        } else {
          const fileData = data as API_FileDataEntity;
          const fileActions = fileData.fileActions || {};
          const viewers = fileData.viewers || {};

          if (fileData.linkedMailId) {
            oneFileItems.push({
              key: "linked_mail",
              label: (
                <>
                  <Tooltip title={linkedMailToolTipText}>
                    <span className="mint">
                      <FontAwesomeIcon icon={["far", "envelope"]} fixedWidth={true} className="mr-5" />
                      {t("Open linked email")}
                    </span>
                  </Tooltip>
                </>
              ),
            });
          }
          // One file
          if (viewers) {
            if (viewers.bcfViewer) {
              oneFileItems.push({
                key: "bcf_view",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("Open with BCF Viewer")}
                  </>
                ),
              });
            }
            if (viewers.webViewer) {
              oneFileItems.push({
                key: "apryse_view",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with WebViewer")}
                  </>
                ),
              });
            } else if (viewers.easyViewer) {
              oneFileItems.push({
                key: "easy_view",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Easy Viewer")}
                  </>
                ),
              });
            } else if (viewers.office365) {
              oneFileItems.push({
                key: "wopi_view",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Office Online")}
                  </>
                ),
              });
              if (viewers.office365Edit) {
                oneFileItems.push({
                  key: "wopi_edit",
                  label: (
                    <>
                      <FontAwesomeIcon icon={["far", "edit"]} fixedWidth={true} className="mr-5" />
                      {t("Edit with Office Online")}
                    </>
                  ),
                });
              }
            } else if (viewers.onlineViewer) {
              oneFileItems.push({
                key: "online_view",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Online Viewer")}
                  </>
                ),
              });
            }
            if (viewers.bimViewerSupported) {
              oneFileItems.push({
                key: "xkt_view",
                disabled: !viewers.bimViewer,
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "cube"]} fixedWidth={true} className="mr-5" />
                    {viewers.bimViewer ? t("View with BIM Viewer") : t("BIM Viewer (being processed)")}
                  </>
                ),
              });
            }
            if (viewers.onlineViewerCompare) {
              oneFileItems.push({
                key: "online_view_compare",
                label: (
                  <>
                    <FontAwesomeIcon icon={["fas", "not-equal"]} fixedWidth={true} className="mr-5" />
                    {t("Compare with...")}
                  </>
                ),
              });
            }
          }
          if (
            fileActions.lock &&
            (!allowedKeys.length || allowedKeys.includes("unlock") || allowedKeys.includes("lock"))
          ) {
            if (fileData.state === "LOCKED") {
              oneFileItems.push({
                key: "unlock",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "lock-open"]} fixedWidth={true} className="mr-5" />
                    {t("Disable write lock")}
                  </>
                ),
              });
            } else {
              oneFileItems.push({
                key: "lock",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "lock"]} fixedWidth={true} />
                    {t("Enable write lock")}
                  </>
                ),
              });
            }
          }

          // Basic
          if (fileActions.download || fileActions.downloadWithWatermark) {
            basicItems.push({
              key: "download",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "download"]} fixedWidth={true} className="mr-5" />
                  {t("Download") + "..."}
                </>
              ),
            });
          }
          if (fileActions.collectionFolder && (!allowedKeys.length || allowedKeys.includes("collection_folder"))) {
            basicItems.push({
              key: "collection_folder",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "folder-open"]} fixedWidth={true} className="mr-5" />
                  {t("Add to collection folder")}
                </>
              ),
            });
          }
          if (fileActions.move && (!allowedKeys.length || allowedKeys.includes("move"))) {
            basicItems.push({
              key: "move",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "dolly"]} fixedWidth={true} className="mr-5" />
                  {t("Move")}...
                </>
              ),
            });
          }
          if (fileActions.changeFilePosition) {
            basicItems.push({
              key: "change_position",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "sort"]} fixedWidth={true} className="mr-5" />
                  {t("Change position")}
                </>
              ),
            });
          }
          if (fileActions.createSymlink && (!allowedKeys.length || allowedKeys.includes("createSymlink"))) {
            basicItems.push({
              key: "create_symlink",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "link"]} fixedWidth={true} className="mr-5" />
                  {t("Create link")}...
                </>
              ),
            });
          }
          if (fileActions.changeFileOwner) {
            basicItems.push({
              key: "change_owner",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "user"]} fixedWidth={true} className="mr-5" />
                  {t("Change file owner")}...
                </>
              ),
            });
          }
          if (fileActions.jumpToDirectory && (!allowedKeys.length || allowedKeys.includes("jump_to_dir"))) {
            basicItems.push({
              key: "jump_to_dir",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "folder"]} fixedWidth={true} />
                  {t("Jump to directory")}
                </>
              ),
            });
          }
          if (fileActions.editLabels && projectsDataStore.functions?.includes("FILE_LABELS")) {
            basicItems.push({
              key: "labels",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "tag"]} fixedWidth={true} />
                  {t("Labels")}...
                </>
              ),
            });
          }
          if (fileActions.restore) {
            basicItems.push({
              key: "restore",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "trash-restore-alt"]} fixedWidth={true} className="mr-5" />
                  {t("Restore")}
                </>
              ),
            });
          }
          if (fileData.state !== "DELETED" && fileData.state !== "EXTERN_SYMLINK") {
            if (fileData.unread) {
              basicItems.push({
                key: "read",
                label: (
                  <>
                    <FontAwesomeIcon icon={["fal", "bold"]} fixedWidth={true} className="mr-5" />
                    {t("Mark as read")}
                  </>
                ),
              });
            } else {
              basicItems.push({
                key: "unread",
                label: (
                  <>
                    <FontAwesomeIcon icon={["fas", "bold"]} fixedWidth={true} className="mr-5" />
                    {t("Mark as unread")}
                  </>
                ),
              });
            }
          }
          if (fileActions.delete && (!allowedKeys.length || allowedKeys.includes("delete"))) {
            if (fileData.state === "SYMLINK" || fileData.state === "EXTERN_SYMLINK") {
              basicItems.push({
                key: "delete_link",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "unlink"]} fixedWidth={true} className="mr-5" />
                    {t("Remove link")}
                  </>
                ),
              });
            } else {
              basicItems.push({
                key: "delete",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} className="mr-5" />
                    {t("Mark as deleted")}
                  </>
                ),
              });
            }
          }

          // Print job & other modules
          if (fileActions.print) {
            moduleItems.push({
              key: "print",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "print"]} fixedWidth={true} className="mr-5" />
                  {t("Print job")}...
                </>
              ),
            });
          }
          if (fileData.state !== "DELETED") {
            if (fileActions.send) {
              moduleItems.push({
                key: "send",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "envelope"]} fixedWidth={true} />
                    {t("Send")}...
                  </>
                ),
              });
            }
            if (fileActions.addToTask) {
              moduleItems.push({
                key: "task",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "tasks"]} fixedWidth={true} className="mr-5" />
                    {t("Create a task")}...
                  </>
                ),
              });
            }
            if (fileActions.askQuestion) {
              moduleItems.push({
                key: "ask_q",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "comments"]} fixedWidth={true} />
                    {t("Ask question on this file")}...
                  </>
                ),
              });
            }
            if (fileActions.workflow) {
              moduleItems.push({
                key: "start_workflow",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "code-branch"]} fixedWidth={true} />
                    {t("Start workflow")}...
                  </>
                ),
              });
            }
          }
        }

        const result: ItemType[] = [
          showFolderActions &&
            folderItems.length > 0 && {
              type: "group" as const,
              key: "folderItems",
              label: t("Directory"),
              children: folderItems,
            },
          showOneFileActions &&
            oneFileItems.length > 0 && {
              type: "group" as const,
              key: "oneFileItems",
              label: t("This file"),
              children: oneFileItems,
            },
          showBasicFileActions &&
            basicItems.length > 0 && {
              type: "group" as const,
              key: "basicItems",
              label: "Basics",
              children: basicItems,
            },
          showModuleFileActions &&
            moduleItems.length > 0 && {
              type: "group" as const,
              key: "moduleItems",
              label: t("Printing and other modules"),
              children: moduleItems,
            },
        ].filter(Boolean);

        return result;
      }, [data, showFolderActions, showOneFileActions, showBasicFileActions, showModuleFileActions]);

      if (menuItems.length === 0 && isFolderItem(data)) {
        return <span className="color-pale-grey">{children}</span>;
      }

      return (
        <Dropdown
          menu={{
            items: menuItems,
            onClick: handleMenuClick,
          }}
          autoAdjustOverflow={true}
          trigger={["click"]}
          destroyOnHidden={true}
          popupRender={dropdownPositionFix}
        >
          {children}
        </Dropdown>
      );
    }
  )
);
